import React, { useState, useEffect, useCallback } from 'react';
import { quotationsAPI, costingsAPI, partsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Search } from 'lucide-react';
import { QuotationItemsTable } from './QuotationItemsTable';
import { ManualItemForm } from './ManualItemForm';
import { PartItemForm } from './PartItemForm';

const INITIAL_MANUAL_FORM = {
  part_internal_no: '', description: '', net_weight_grams: '',
  quantity: 1000, quantity_unit: 'pcs', uom_text: '',
  unit_price: 0, price_unit: 1, production_lead_time: '',
  sample_lead_time: '', tooling_cost: 0, sample_cost: 0, remarks: '',
};

const INITIAL_ITEM_FORM = {
  quantity: 1000, quantity_unit: 'pcs', uom: 'pcs', price_unit: 1,
  unit_price_override: null, production_lead_time: '', sample_lead_time: '',
  tooling_cost: 0, sample_cost: 0, remarks: '',
};

function QuotationItemsBuilder({ quotationId, quotation, setQuotation, onComplete }) {
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedCosting, setSelectedCosting] = useState(null);
  const [availableCostings, setAvailableCostings] = useState([]);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [manualItemForm, setManualItemForm] = useState(INITIAL_MANUAL_FORM);
  const [itemForm, setItemForm] = useState(INITIAL_ITEM_FORM);
  const [adding, setAdding] = useState(false);

  const searchParts = useCallback(async () => {
    try {
      const res = await partsAPI.list({ search: partSearch, is_active: true, limit: 20 });
      setPartResults(res.data?.items || res.data?.data || []);
    } catch (e) { }
  }, [partSearch]);

  useEffect(() => {
    if (partSearch.length >= 2) {
      searchParts();
    } else {
      setPartResults([]);
    }
  }, [partSearch, searchParts]);

  const fetchCostingsForPart = async (partId) => {
    try {
      const res = await costingsAPI.list({ part_id: partId, is_active: true, limit: 100 });
      const costings = res.data?.items || res.data?.data || [];
      setAvailableCostings(costings);
      return costings;
    } catch (e) {
      return [];
    }
  };

  const refreshQuotation = async () => {
    const qRes = await quotationsAPI.get(quotationId);
    setQuotation(qRes.data);
  };

  const selectPartForAdd = async (part) => {
    setSelectedPart(part);
    const partCostings = await fetchCostingsForPart(part.id);
    let activeCosting = part.active_costing_id
      ? partCostings.find(c => c.id === part.active_costing_id) 
      : null;
    if (!activeCosting && partCostings.length > 0) activeCosting = partCostings[0];
    setSelectedCosting(activeCosting);

    let basePrice = 0;
    if (activeCosting) {
      basePrice = activeCosting.selling_price_per_piece || activeCosting.total_cost_per_piece || 0;
    } else if (part.part_category === 'kit') {
      basePrice = part.suggested_price || part.kit_total_cost || 0;
    }

    setItemForm({
      ...INITIAL_ITEM_FORM,
      quantity: activeCosting?.quantity || 1000,
      unit_price_override: basePrice,
    });
    setAddItemDialog(true);
    setPartSearch('');
    setPartResults([]);
  };

  const handleCostingChange = (costingId) => {
    const newCosting = availableCostings.find(c => c.id === costingId);
    if (newCosting) {
      setSelectedCosting(newCosting);
      setItemForm(prev => {
        const isKg = prev.quantity_unit === 'kg';
        const weight = newCosting.net_weight_grams || selectedPart?.net_weight_grams || 0;
        const kgRate = newCosting.selling_price_per_kg
          || (weight ? (newCosting.selling_price_per_piece * 1000) / weight : 0);
        return {
          ...prev,
          quantity: newCosting.quantity || prev.quantity,
          unit_price_override: isKg
            ? Number((kgRate || 0).toFixed(4))
            : (newCosting.selling_price_per_piece || 0),
        };
      });
    }
  };

  const openManualItemDialog = () => {
    setIsManualEntry(true);
    setSelectedPart(null);
    setSelectedCosting(null);
    setManualItemForm(INITIAL_MANUAL_FORM);
    setAddItemDialog(true);
  };

  const addManualItem = async () => {
    if (!manualItemForm.description) { toast.error('Description is required'); return; }
    if (!manualItemForm.unit_price || manualItemForm.unit_price <= 0) { toast.error('Unit price is required'); return; }
    setAdding(true);
    try {
      const itemData = {
        is_manual: true, description: manualItemForm.description,
        part_internal_no: manualItemForm.part_internal_no || '',
        net_weight_grams: parseFloat(manualItemForm.net_weight_grams) || null,
        quantity: parseFloat(manualItemForm.quantity) || 1000,
        quantity_unit: manualItemForm.quantity_unit,
        uom_text: manualItemForm.uom_text || null, uom: 'pcs',
        price_unit: parseInt(manualItemForm.price_unit) || 1,
        unit_price_override: parseFloat(manualItemForm.unit_price) || 0,
        production_lead_time: manualItemForm.production_lead_time || null,
        sample_lead_time: manualItemForm.sample_lead_time || null,
        tooling_cost: parseFloat(manualItemForm.tooling_cost) || 0,
        sample_cost: parseFloat(manualItemForm.sample_cost) || 0,
        remarks: manualItemForm.remarks || null,
      };
      await quotationsAPI.addItems(quotationId, { items: [itemData] });
      toast.success('Manual item added');
      setAddItemDialog(false);
      setIsManualEntry(false);
      await refreshQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add manual item');
    } finally { setAdding(false); }
  };

  const addItem = async () => {
    if (!selectedPart) return;
    setAdding(true);
    try {
      const itemData = {
        quantity: itemForm.quantity, quantity_unit: itemForm.quantity_unit,
        uom: itemForm.uom, price_unit: itemForm.price_unit || 1,
        unit_price_override: itemForm.unit_price_override,
        production_lead_time: itemForm.production_lead_time || null,
        sample_lead_time: itemForm.sample_lead_time || null,
        tooling_cost: itemForm.tooling_cost || 0,
        sample_cost: itemForm.sample_cost || 0,
        remarks: itemForm.remarks || null,
      };
      if (selectedCosting) { itemData.costing_id = selectedCosting.id; }
      else { itemData.part_id = selectedPart.id; }

      await quotationsAPI.addItems(quotationId, { items: [itemData] });
      toast.success('Item added');
      setAddItemDialog(false);
      setSelectedPart(null);
      setSelectedCosting(null);
      await refreshQuotation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    } finally { setAdding(false); }
  };

  const removeItem = async (itemId) => {
    try {
      await quotationsAPI.removeItem(quotationId, itemId);
      toast.success('Item removed');
      await refreshQuotation();
    } catch (error) { toast.error('Failed to remove item'); }
  };

  const cloneItem = async (item) => {
    try {
      const itemData = {
        quantity: item.quantity, quantity_unit: item.quantity_unit || 'pcs',
        uom: item.uom || 'pcs', price_unit: item.price_unit || 1,
        unit_price_override: item.unit_price,
        production_lead_time: item.production_lead_time || null,
        sample_lead_time: item.sample_lead_time || null,
        tooling_cost: item.tooling_cost || 0, sample_cost: item.sample_cost || 0,
        remarks: item.remarks ? `Copy of: ${item.remarks}` : null,
      };
      if (item.costing_id) { itemData.costing_id = item.costing_id; }
      else { itemData.part_id = item.part_id; }

      await quotationsAPI.addItems(quotationId, { items: [itemData] });
      toast.success('Row cloned - modify quantity and pricing as needed');
      await refreshQuotation();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to clone item'); }
  };

  const formatCurrency = (amount) => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    const currency = quotation?.currency || 'INR';
    return `${symbols[currency] || currency}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      {/* Quotation Header Summary */}
      <Card className="card-dashboard bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Quotation</p>
              <p className="font-mono font-bold text-lg">{quotation?.quotation_number}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Customer</p>
              <p className="font-medium">{quotation?.customer_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Currency</p>
              <Badge variant="outline" className="font-mono">
                {quotation?.currency}
                {quotation?.exchange_rate !== 1 && ` @${quotation?.exchange_rate}`}
              </Badge>
            </div>
            <div className="ml-auto">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="font-mono font-bold text-xl text-primary">{formatCurrency(quotation?.total_amount)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Section */}
      <Card className="card-dashboard">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-heading uppercase">Line Items (Shopping Cart)</CardTitle>
            <CardDescription>Search parts to add to the quotation (Kits and non-costed parts supported)</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {/* Part Search + Manual Entry */}
          <div className="mb-4 flex gap-3 items-end">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search parts by part number or description..."
                value={partSearch}
                onChange={(e) => setPartSearch(e.target.value)}
                className="pl-9"
                data-testid="part-search-input"
              />
              {partResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 border rounded-lg bg-card max-h-60 overflow-y-auto shadow-lg">
                  {partResults.map((p) => (
                    <div key={p.id} className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0" onClick={() => { setIsManualEntry(false); selectPartForAdd(p); }} data-testid={`part-result-${p.internal_part_no}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-mono font-medium">{p.internal_part_no}</span>
                          {p.part_category === 'kit' && <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">Kit</Badge>}
                        </div>
                        <div className="text-right">
                          {p.active_costing_id ? <Badge variant="outline" className="text-green-600 border-green-300 text-xs">Costed</Badge> : <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">No Costing</Badge>}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                      <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                        {p.grade && <span>Grade: {p.grade}</span>}
                        {p.finish && <span>• {p.finish}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button type="button" variant="outline" onClick={openManualItemDialog} className="whitespace-nowrap" data-testid="add-manual-quote-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Manual Entry
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Search parts from Part Master or click &quot;Manual Entry&quot; for legacy/spot quotes without master data
          </p>

          <QuotationItemsTable quotation={quotation} formatCurrency={formatCurrency} cloneItem={cloneItem} removeItem={removeItem} />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onComplete}>Save & View Quotation</Button>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialog} onOpenChange={(open) => { setAddItemDialog(open); if (!open) setIsManualEntry(false); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{isManualEntry ? 'Add Manual Line Item' : 'Add Line Item'}</DialogTitle>
            <DialogDescription>{isManualEntry ? 'Enter item details manually (for legacy/spot quotes without Part Master)' : 'Configure quantity and pricing for this item'}</DialogDescription>
          </DialogHeader>
          {isManualEntry && <ManualItemForm form={manualItemForm} setForm={setManualItemForm} />}
          {selectedPart && !isManualEntry && (
            <PartItemForm
              selectedPart={selectedPart}
              selectedCosting={selectedCosting}
              availableCostings={availableCostings}
              itemForm={itemForm}
              setItemForm={setItemForm}
              onCostingChange={handleCostingChange}
              quotationId={quotationId}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddItemDialog(false); setIsManualEntry(false); }}>Cancel</Button>
            {isManualEntry ? (
              <Button className="btn-primary" onClick={addManualItem} disabled={adding} data-testid="add-manual-to-quote-btn">
                {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Add Manual Item
              </Button>
            ) : (
              <Button className="btn-primary" onClick={addItem} disabled={adding} data-testid="add-to-quote-btn">
                {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Add to Quote
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QuotationItemsBuilder;
